// Loads MapLibre GL from CDN on first use and builds the basemap style. The
// widget ships zero map bytes in its own bundle (the mapbox-gl lesson):
// MapLibre is only ever downloaded when a map template actually renders.

const MAPLIBRE_VERSION = "5.24.0";
const JS_URL = `https://unpkg.com/maplibre-gl@${MAPLIBRE_VERSION}/dist/maplibre-gl.js`;
const CSS_URL = `https://unpkg.com/maplibre-gl@${MAPLIBRE_VERSION}/dist/maplibre-gl.css`;
const JS_INTEGRITY =
  "sha384-5+cfbwT0iiub6VsQAdn6yz16nr6sDiQoHx6tm4O8OVYXHYOxcffFmCJBL0dgdvGp";
const CSS_INTEGRITY =
  "sha384-uTttxo/aOKbdE5RlD/SPzSDoDmNvGlUYPjONi2MN/b7c9HPSvW07OIuyP7uL6jxK";

// The style, sprite and glyphs live in public/map/ on the Firebase host that
// serves this bundle (see scripts/fetch-map-assets.mjs). On a customer site
// the bundle runs inside a shadow root, where document.currentScript is null,
// so the host is named here instead of discovered.
const assetBase = () => {
  if (process.env.NODE_ENV === "development") return window.location.origin;
  return process.env.REACT_APP_IS_PROD === "yes"
    ? "https://widgets-plutonios-prod.web.app"
    : "https://widgets-plutonios-dev.web.app";
};

let scriptPromise: Promise<any> | null = null;
let stylePromise: Promise<any> | null = null;
const cssPromises = new WeakMap<Node, Promise<void>>();

// MapLibre positions its canvas, markers and controls from its stylesheet. On
// a customer site the widget is mounted inside a shadow root, and a <link> in
// document.head does not cross that boundary: markers fall back to normal
// flow and the controls pile up in a corner. So the stylesheet goes into the
// map's own root node, whichever that is.
const ensureCss = (root: Document | ShadowRoot): Promise<void> => {
  const cached = cssPromises.get(root);
  if (cached) return cached;

  const parent: ParentNode =
    root instanceof ShadowRoot ? root : root.head ?? document.head;

  const promise = new Promise<void>((resolve, reject) => {
    if (parent.querySelector(`link[href="${CSS_URL}"]`)) {
      resolve();
      return;
    }
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = CSS_URL;
    css.integrity = CSS_INTEGRITY;
    css.crossOrigin = "";
    css.onload = () => resolve();
    // An unstyled map is precisely the broken map this exists to prevent, so
    // a missing stylesheet counts as a failed load and the caller falls back
    // to the plain embed.
    css.onerror = () => {
      cssPromises.delete(root);
      reject(new Error("Failed to load MapLibre CSS"));
    };
    parent.appendChild(css);
  });

  cssPromises.set(root, promise);
  return promise;
};

const ensureScript = (): Promise<any> => {
  if ((window as any).maplibregl?.Map)
    return Promise.resolve((window as any).maplibregl);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = JS_URL;
    script.integrity = JS_INTEGRITY;
    script.crossOrigin = "";
    script.async = true;
    script.onload = () => {
      const maplibregl = (window as any).maplibregl;
      if (maplibregl?.Map) resolve(maplibregl);
      else reject(new Error("MapLibre loaded but window.maplibregl is missing"));
    };
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error("Failed to load MapLibre"));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
};

const ensureStyle = (): Promise<any> => {
  if (stylePromise) return stylePromise;
  const base = assetBase();
  stylePromise = fetch(`${base}/map/voyager.json`)
    .then((res) => {
      if (!res.ok) throw new Error(`Map style: HTTP ${res.status}`);
      return res.json();
    })
    .then((style) => ({
      ...style,
      sprite: style.sprite.replace("{base}", base),
      glyphs: style.glyphs.replace("{base}", base),
    }))
    .catch((err) => {
      stylePromise = null;
      throw err;
    });
  return stylePromise;
};

// `target` is the element the map will be built in: it decides which root the
// stylesheet has to land in. Resolves only once all three pieces are in place:
// MapLibre measures the container on creation, and measuring it unstyled is
// how the map ends up mispositioned.
export const loadMapLibre = (
  target?: Node | null
): Promise<{ maplibregl: any; style: any }> => {
  const root = (target?.getRootNode?.() ?? document) as Document | ShadowRoot;
  return Promise.all([ensureScript(), ensureCss(root), ensureStyle()]).then(
    ([maplibregl, , style]) => ({ maplibregl, style })
  );
};
